import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FacebookShareButton, FacebookIcon,
  XShareButton, XIcon,
} from 'react-share';
import html2canvas from 'html2canvas';
import { getPostcard } from '../api/postcardService';
import { getViewerToken } from '@features/auth/api/authService';
import { getStorage, STORAGE_KEYS, getUserInfo } from '@shared/api/client';
import PostcardCanvas from './PostcardCanvas';
import { ROUTES } from '@app/router/routes';
import CloseButton from '@/shared/components/CloseButton';
import { useLanguage } from '@shared/contexts/LanguageContext';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

export default function PostcardViewModal({ isOpen, onClose, onMakePostcard, shareCode, recordId, openShare: initialOpenShare = false }) {
  const isMobile = useIsMobile();
  const { t } = useLanguage();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [copyState, setCopyState] = useState('idle');
  const [downloading, setDownloading] = useState(false);
  const [showSharePopup, setShowSharePopup] = useState(initialOpenShare);
  const [user, setUser] = useState(() => getUserInfo());
  const canvasRef = useRef(null);

  useEffect(() => {
    const handler = () => setUser(getUserInfo());
    window.addEventListener('auth-change', handler);
    return () => window.removeEventListener('auth-change', handler);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setCard(null);
    setLoading(true);
    setNotFound(false);
    setLoadError(false);
    setShowSharePopup(initialOpenShare);

    const payload = shareCode
      ? { share_code: shareCode }
      : recordId
        ? { record_id: recordId }
        : null;
    if (!payload) { setLoading(false); setNotFound(true); return; }

    const fetchCard = () => {
      getPostcard(payload)
        .then(setCard)
        .catch((err) => {
          const status = err?.status ?? err?.data?.result_code;
          if (status === 404) setNotFound(true);
          else setLoadError(true);
        })
        .finally(() => setLoading(false));
    };

    const memberToken = getStorage(STORAGE_KEYS.MEMBER_TOKEN);
    if (!memberToken) {
      getViewerToken().catch(() => { }).finally(fetchCard);
    } else {
      fetchCard();
    }
  }, [isOpen, shareCode, recordId]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const shareUrl = shareCode ? `${window.location.origin}/card/${shareCode}` : '';
  const shareTitle = card
    ? `${card.from_name} ${t('postcard-view.sentTo').replace('{{name}}', card.to_name)} — 中台山35週年`
    : '中台山35週年祝福明信片';

  async function proxyImages(el) {
    const imgs = Array.from(el.querySelectorAll('img'));
    const restores = await Promise.all(imgs.map(async (img) => {
      const src = img.getAttribute('src') || '';
      if (!src || src.startsWith('blob:') || src.startsWith('data:') || src.startsWith('/')) return null;
      try {
        const res = await fetch(src);
        const blob = await res.blob();
        const blobUrl = URL.createObjectURL(blob);
        img.src = blobUrl;
        return () => { URL.revokeObjectURL(blobUrl); img.src = src; };
      } catch { return null; }
    }));
    return () => restores.forEach(r => r?.());
  }

  async function handleDownload() {
    if (!canvasRef.current || downloading) return;
    setDownloading(true);
    try {
      const el = canvasRef.current;
      const restore = await proxyImages(el);
      try {
        const canvas = await html2canvas(el, { scale: 3, useCORS: false, backgroundColor: null });
        const link = document.createElement('a');
        link.download = `祝福明信片_${shareCode}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
      } finally { restore(); }
    } finally { setDownloading(false); }
  }

  async function handleCopyLink() {
    let success = false;
    if (navigator.clipboard?.writeText) {
      try { await navigator.clipboard.writeText(shareUrl); success = true; } catch { }
    }
    if (!success) {
      try {
        const ta = document.createElement('textarea');
        ta.value = shareUrl;
        ta.style.cssText = 'position:fixed;top:0;left:0;opacity:0;font-size:16px';
        document.body.appendChild(ta);
        ta.focus();
        ta.setSelectionRange(0, ta.value.length);
        success = document.execCommand('copy');
        document.body.removeChild(ta);
      } catch { }
    }
    if (success) {
      setCopyState('ok');
      setTimeout(() => setCopyState('idle'), 2500);
    } else {
      setCopyState('fail');
      setTimeout(() => setCopyState('idle'), 3000);
    }
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] bg-[#FAF7F3] flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 bg-gradient-to-b from-[#F5EFE6] to-[#FAF7F3] border-b border-[#E8DCC8] px-4 py-8 text-center relative">
        <CloseButton btnColor="text-[#B39029]" onClick={onClose} />
        <p className="text-xs tracking-[0.3em] text-[#B39029] mb-2 uppercase">35th Anniversary</p>

        {card && !card.is_owner && (
          <>
            <h1 className="text-xl font-bold text-[#3E362E]">
              {t('postcard-view.blessingFrom').replace('{{name}}', card.from_name)}
            </h1>
            <p className="text-sm text-[#6E625B] mt-1">{t('postcard-view.sentTo').replace('{{name}}', card.to_name)}</p>
          </>
        )}
        {card?.is_owner && (
          <div className="flex justify-center items-center gap-3 mt-6">
            {[t('postcard.stepSelectDesign'), t('postcard.stepFillContent'), t('postcard.share')].map((label, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 cursor-default">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-all ${i < 2 ? 'bg-[#B39029] text-white' : 'bg-[#907654] text-white shadow-md scale-110'}`}>
                    {i < 2 ? (
                      <svg viewBox="0 0 12 12" className="w-3 h-3" fill="none">
                        <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    ) : 3}
                  </span>
                  <span className={`text-sm font-medium ${i < 2 ? 'text-[#B39029]' : 'text-[#907654]'}`}>{label}</span>
                </div>
                {i < 2 && <div className="w-6 h-px bg-[#B39029]" />}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        {loading && (
          <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
            <div className="w-10 h-10 border-2 border-[#907654] border-t-transparent rounded-full animate-spin" />
            <p className="text-sm text-[#6E625B]">{t('postcard-view.loading')}</p>
          </div>
        )}

        {loadError && (
          <div className="flex items-center justify-center min-h-[60vh] px-4">
            <div className="bg-white rounded-2xl p-10 text-center max-w-sm w-full shadow-sm border border-[#F0EAE4]">
              <div className="w-16 h-16 rounded-full bg-[#F5EFE6] flex items-center justify-center mx-auto mb-4">
                <span className="material-symbols-outlined text-3xl text-[#907654]">wifi_off</span>
              </div>
              <h2 className="text-lg font-bold text-[#3E362E] mb-2">{t('postcard-view.loadErrorTitle')}</h2>
              <p className="text-sm text-[#6E625B] mb-6">{t('postcard-view.loadErrorDesc')}</p>
              <button onClick={() => window.location.reload()} className="bg-[#907654] hover:bg-[#7a6346] text-white rounded-xl px-6 py-2.5 text-sm font-medium cursor-pointer transition-colors">
                {t('postcard-view.reload')}
              </button>
            </div>
          </div>
        )}

        {notFound && (
          <div className="flex items-center justify-center min-h-[60vh] px-4">
            <div className="bg-white rounded-2xl p-10 text-center max-w-sm w-full shadow-sm border border-[#F0EAE4]">
              <div className="w-16 h-16 rounded-full bg-[#F5EFE6] flex items-center justify-center mx-auto mb-4">
                <span className="material-symbols-outlined text-3xl text-[#907654]">search_off</span>
              </div>
              <h2 className="text-lg font-bold text-[#3E362E] mb-2">{t('postcard-view.notFoundTitle')}</h2>
              <p className="text-sm text-[#6E625B] mb-6">{t('postcard-view.notFoundDesc')}</p>
              <button onClick={onClose} className="bg-[#907654] hover:bg-[#7a6346] text-white rounded-xl px-6 py-2.5 text-sm font-medium cursor-pointer transition-colors">
                {t('postcard-view.close')}
              </button>
            </div>
          </div>
        )}

        {card && (
          <div className="px-4 py-6 space-y-4">
            {!isMobile && (
              <div className="max-w-5xl mx-auto space-y-3">
                <PostcardCanvas
                  item={{ desktop_url: card.desktop_url, mobile_url: card.mobile_url, is_full_bleed: card.is_full_bleed, name: card.name }}
                  fromName={card.from_name} toName={card.to_name} content={card.content}
                  isMobile={false} forwardRef={canvasRef}
                />
              </div>
            )}
            {isMobile && (
              <div className="-mx-4">
                <PostcardCanvas
                  item={{ desktop_url: card.desktop_url, mobile_url: card.mobile_url, is_full_bleed: card.is_full_bleed, name: card.name }}
                  fromName={card.from_name} toName={card.to_name} content={card.content}
                  isMobile={true} forwardRef={canvasRef}
                />
              </div>
            )}

            {card.is_owner && (
              <div className={`flex gap-3 ${isMobile ? 'max-w-sm mx-auto' : 'max-w-5xl mx-auto'}`}>
                <button
                  onClick={() => setShowSharePopup(true)}
                  className="w-full flex items-center justify-center gap-2 border border-[#E8DCC8] text-[#6E625B] hover:bg-[#FAF7F3] transition-colors rounded-xl py-3 text-sm font-bold cursor-pointer"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
                    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                  </svg>
                  {t('postcard-view.share')}
                </button>
                <button
                  onClick={onClose}
                  className="w-full flex items-center justify-center gap-2 bg-[#B39029] hover:bg-[#9a7a22] text-white rounded-xl py-3 text-sm font-bold cursor-pointer transition-colors shadow-md"
                >
                  {t('postcard-view.done')}
                </button>
              </div>
            )}

            {!card.is_owner && (
              <div className="max-w-2xl mx-auto space-y-4">
                <div className="bg-gradient-to-br from-[#F5EFE6]/95 to-[#FAF7F3]/95 backdrop-blur-sm rounded-2xl p-4 md:p-6 border border-[#E8DCC8] text-center space-y-2 md:space-y-3">
                  <p className="text-sm font-medium text-[#3E362E]">{t('postcard-view.wantToSend')}</p>
                  <p className="text-xs text-[#6E625B]">{t('postcard-view.makePostcardHint')}</p>
                  <button
                    onClick={onMakePostcard ?? onClose}
                    className="inline-block bg-[#B39029] hover:bg-[#9a7a22] text-white rounded-xl px-8 py-2.5 text-sm font-bold cursor-pointer transition-colors shadow-sm"
                  >
                    {t('postcard-view.makePostcard')}
                  </button>
                </div>
                <p className="text-center text-xs text-[#6E625B]/60 pb-2">Copyright © 2026 Chung Tai Chan Monastery</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Share Popup */}
      {showSharePopup && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center px-4" onClick={() => setShowSharePopup(false)}>
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          <div className="relative bg-white rounded-2xl w-full max-w-md p-6 space-y-5 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-[#B39029]" />
                <h2 className="font-bold text-[#3E362E]">{t('postcard-view.sharePopupTitle')}</h2>
              </div>
              <CloseButton btnColor="text-[#C5A596]" onClick={() => setShowSharePopup(false)} />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#6E625B] mb-1.5">{t('postcard-view.copyLinkLabel')}</label>
              <div className="flex gap-2">
                <input
                  readOnly value={shareUrl} onFocus={e => e.target.select()}
                  className="flex-1 bg-[#FAF7F3] border border-[#E8DCC8] rounded-xl px-3 py-2.5 w-full text-sm text-[#6E625B] truncate"
                />
                <button
                  onClick={handleCopyLink}
                  className={`shrink-0 rounded-xl px-4 py-2.5 text-sm font-medium cursor-pointer transition-all ${copyState === 'ok' ? 'bg-green-500 text-white' :
                      copyState === 'fail' ? 'bg-orange-400 text-white' :
                        'bg-[#907654] hover:bg-[#7a6346] text-white'
                    }`}
                >
                  {copyState === 'ok' ? (
                    <span className="flex items-center gap-1.5">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      {t('postcard-view.copied')}
                    </span>
                  ) : t('postcard-view.copy')}
                </button>
              </div>
              {copyState === 'fail' && <p className="mt-2 text-xs text-orange-500">{t('postcard-view.copyFailed')}</p>}
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex flex-col items-center gap-1">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const url = `https://line.me/R/msg/text/?${encodeURIComponent(`${shareTitle}\n${shareUrl}`)}`;
                    const newWin = window.open(url, '_blank');
                    if (!newWin || newWin.closed || typeof newWin.closed === 'undefined') window.location.href = url;
                  }}
                  className="w-12 h-12 rounded-full flex items-center justify-center"
                  style={{ background: '#06C755' }}
                  aria-label="分享到 LINE"
                >
                  <svg role="img" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" className="w-8 h-8">
                    <path fill="white" d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314" />
                  </svg>
                </button>
                <span className="text-[10px] text-[#6E625B] opacity-60">LINE</span>
              </div>
              <div className="flex flex-col items-center gap-1">
                <FacebookShareButton url={shareUrl} quote={shareTitle} onClick={(e) => e.stopPropagation()}>
                  <FacebookIcon size={48} round />
                </FacebookShareButton>
                <span className="text-[10px] text-[#6E625B] opacity-60">Facebook</span>
              </div>
              <div className="flex flex-col items-center gap-1">
                <XShareButton url={shareUrl} title={shareTitle} onClick={(e) => e.stopPropagation()}>
                  <XIcon size={48} round />
                </XShareButton>
                <span className="text-[10px] text-[#6E625B] opacity-60">X</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
